import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  Typography,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CloudUpload as UploadIcon,
  Storage as DataIcon,
  People as PeopleIcon,
  NotInterested as RejectedIcon,
} from '@mui/icons-material';
import HistoryIcon from '@mui/icons-material/History';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import CampaignIcon from '@mui/icons-material/Campaign';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { marketplaceApi, MarketplaceCatalogItem } from '../../api/marketplace.api';

interface SidebarProps {
  drawerWidth: number;
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
  collapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ drawerWidth, mobileOpen, handleDrawerToggle, collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const marketplaceActive = location.pathname.startsWith('/marketplace');
  const [marketplaceOpen, setMarketplaceOpen] = useState(marketplaceActive);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', roles: ['ADMIN', 'SUPER_ADMIN', 'CONTRIBUTOR'] },
    { text: 'Upload CSV', icon: <UploadIcon />, path: '/upload', roles: ['SUPER_ADMIN', 'CONTRIBUTOR'] },
    { text: 'Data Management', icon: <DataIcon />, path: '/data', roles: ['ADMIN', 'SUPER_ADMIN', 'CONTRIBUTOR'] },
    { text: 'Data Finalization', icon: <TaskAltIcon />, path: '/data-final', roles: ['SUPER_ADMIN', 'CONTRIBUTOR'] },
    { text: 'Pushed Data', icon: <SendIcon />, path: '/pushed-data', roles: ['SUPER_ADMIN', 'CONTRIBUTOR'] },
    { text: 'Rejected Data', icon: <RejectedIcon />, path: '/rejected-data', roles: ['ADMIN', 'SUPER_ADMIN', 'CONTRIBUTOR'] },
    { text: 'Users', icon: <PeopleIcon />, path: '/users', roles: ['SUPER_ADMIN'] },
    { text: 'Activity Log', icon: <HistoryIcon />, path: '/activity-log', roles: ['SUPER_ADMIN'] },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['SUPER_ADMIN'] },
  ];

  const [customMarketplaces, setCustomMarketplaces] = useState<MarketplaceCatalogItem[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const pendingPath = useRef<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameSlug, setRenameSlug] = useState('');
  const [renameName, setRenameName] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ slug: string; name: string; path: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [notice, setNotice] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const loadCustomMarketplaces = () => {
    if (user?.role !== 'SUPER_ADMIN') return;
    marketplaceApi.listCatalog()
      .then(setCustomMarketplaces)
      .catch(() => setCustomMarketplaces([]));
  };

  useEffect(() => {
    loadCustomMarketplaces();
    const onChange = () => loadCustomMarketplaces();
    const onNotice = (event: Event) => {
      const detail = (event as CustomEvent).detail as { message?: string; severity?: 'success' | 'error' } | undefined;
      if (!detail?.message) return;
      setNotice({ message: detail.message, severity: detail.severity || 'success' });
    };
    window.addEventListener('marketplace-catalog-changed', onChange);
    window.addEventListener('marketplace-catalog-notice', onNotice);
    return () => {
      window.removeEventListener('marketplace-catalog-changed', onChange);
      window.removeEventListener('marketplace-catalog-notice', onNotice);
    };
  }, [user?.role]);

  const marketplaceItems = [
    { text: 'Link Publisher', icon: <LinkIcon fontSize="small" />, path: '/marketplace/link-publisher', slug: 'link-publisher', custom: false },
    { text: 'Adsy', icon: <CampaignIcon fontSize="small" />, path: '/marketplace/adsy', slug: 'adsy', custom: false },
    { text: 'Bazoom', icon: <RocketLaunchIcon fontSize="small" />, path: '/marketplace/bazoom', slug: 'bazoom', custom: false },
    ...customMarketplaces.map((item) => ({
      text: item.name,
      icon: <StorefrontIcon fontSize="small" />,
      path: `/marketplace/${item.slug}`,
      slug: item.slug,
      custom: true
    }))
  ];

  const handleCreateMarketplace = async () => {
    const name = createName.trim();
    if (name.length < 2) {
      setCreateError('Enter a name with at least 2 characters');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const created = await marketplaceApi.createCatalog(name);
      setCustomMarketplaces((prev) => [...prev, created]);
      setCreateName('');
      pendingPath.current = `/marketplace/${created.slug}`;
      setCreateOpen(false);
      window.dispatchEvent(new Event('marketplace-catalog-changed'));
    } catch (err: any) {
      setCreateError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not create marketplace'
      );
    } finally {
      setCreating(false);
    }
  };

  const openRename = (slug: string, name: string) => {
    setRenameSlug(slug);
    setRenameName(name);
    setRenameError('');
    setRenameOpen(true);
  };

  const saveRename = async () => {
    const name = renameName.trim();
    if (name.length < 2) {
      setRenameError('Enter a name with at least 2 characters');
      return;
    }
    setRenaming(true);
    setRenameError('');
    try {
      const updated = await marketplaceApi.renameCatalog(renameSlug, name);
      setCustomMarketplaces((prev) => prev.map((item) => (item.slug === updated.slug ? updated : item)));
      setRenameOpen(false);
      setNotice({ message: `Marketplace renamed to "${updated.name}".`, severity: 'success' });
      window.dispatchEvent(new Event('marketplace-catalog-changed'));
    } catch (err: any) {
      setRenameError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not rename marketplace'
      );
    } finally {
      setRenaming(false);
    }
  };

  const confirmDeleteMarketplace = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await marketplaceApi.deleteCatalog(deleteTarget.slug);
      setCustomMarketplaces((prev) => prev.filter((item) => item.slug !== deleteTarget.slug));
      window.dispatchEvent(new Event('marketplace-catalog-changed'));
      setNotice({ message: `"${deleteTarget.name}" and its sites were deleted.`, severity: 'success' });
      if (location.pathname === deleteTarget.path) navigate('/marketplace/link-publisher');
      setDeleteTarget(null);
    } catch (err: any) {
      setNotice({
        message:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Could not delete marketplace',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(user?.role || '')
  );

  const showMarketplace = user?.role === 'SUPER_ADMIN';

  const go = (path: string) => {
    navigate(path);
    if (mobileOpen) handleDrawerToggle();
  };

  const drawer = (
    <Box>
      <Toolbar>
        {!collapsed && (
          <Typography variant="h6" noWrap component="div" fontWeight="bold">
            Hypwave Data Processing
          </Typography>
        )}
        {collapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Typography variant="h6" component="div" fontWeight="bold">
              H
            </Typography>
          </Box>
        )}
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            {collapsed ? (
              <Tooltip title={item.text} placement="right">
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => go(item.path)}
                  sx={{ justifyContent: 'center', px: 2.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => go(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            )}
          </ListItem>
        ))}

        {showMarketplace && (
          <>
            <ListItem disablePadding>
              {collapsed ? (
                <Tooltip title="Marketplace" placement="right">
                  <ListItemButton
                    selected={marketplaceActive}
                    onClick={() => setMarketplaceOpen((v) => !v)}
                    sx={{ justifyContent: 'center', px: 2.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                      <StorefrontIcon />
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              ) : (
                <ListItemButton
                  selected={marketplaceActive}
                  onClick={() => setMarketplaceOpen((v) => !v)}
                >
                  <ListItemIcon>
                    <StorefrontIcon />
                  </ListItemIcon>
                  <ListItemText primary="Marketplace" />
                  {marketplaceOpen || marketplaceActive ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              )}
            </ListItem>
            <Collapse in={collapsed ? marketplaceOpen || marketplaceActive : marketplaceOpen || marketplaceActive} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {marketplaceItems.map((item) => (
                  <ListItem
                    key={item.path}
                    disablePadding
                    secondaryAction={
                      !collapsed && item.custom ? (
                        <Box sx={{ display: 'flex' }}>
                          <Tooltip title="Rename">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={`Rename ${item.text}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openRename(item.slug, item.text);
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              edge="end"
                              aria-label={`Delete ${item.text}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                setDeleteTarget({ slug: item.slug, name: item.text, path: item.path });
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : undefined
                    }
                  >
                    {collapsed ? (
                      <Tooltip title={item.text} placement="right">
                        <ListItemButton
                          selected={location.pathname === item.path}
                          onClick={() => go(item.path)}
                          sx={{ justifyContent: 'center', px: 2.5, pl: 2 }}
                        >
                          <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                            {item.icon}
                          </ListItemIcon>
                        </ListItemButton>
                      </Tooltip>
                    ) : (
                      <ListItemButton
                        selected={location.pathname === item.path}
                        onClick={() => go(item.path)}
                        sx={{ pl: 4, pr: item.custom ? 10 : 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.text} primaryTypographyProps={{ noWrap: true }} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      setCreateError('');
                      setCreateName('');
                      setCreateOpen(true);
                    }}
                    sx={{ pl: collapsed ? 2.5 : 4, justifyContent: collapsed ? 'center' : 'flex-start' }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>
                      <AddIcon fontSize="small" />
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary="Create marketplace" />}
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
      <Dialog
        open={createOpen}
        onClose={() => {
          if (!creating) setCreateOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        disableScrollLock
        TransitionProps={{
          onExited: () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
            document.body.classList.remove('MuiModal-open');
            document.getElementById('root')?.removeAttribute('aria-hidden');
            const path = pendingPath.current;
            pendingPath.current = null;
            if (path) {
              navigate(path);
              if (mobileOpen) handleDrawerToggle();
            }
          }
        }}
      >
        <DialogTitle>Create marketplace</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This adds a new item under Marketplace. Upload uses the same CSV format as Adsy and Bazoom. Its listings stay separate from the other marketplaces.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Marketplace name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateMarketplace();
            }}
            error={!!createError}
            helperText={createError || 'Example: Guest Post Links'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateMarketplace} disabled={creating}>
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={renameOpen}
        onClose={() => !renaming && setRenameOpen(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Rename marketplace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Marketplace name"
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveRename();
            }}
            error={!!renameError}
            helperText={renameError || 'The address stays the same. Only the name changes.'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)} disabled={renaming}>Cancel</Button>
          <Button variant="contained" onClick={saveRename} disabled={renaming}>
            {renaming ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Delete marketplace</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong> and all of its sites? This cannot be undone. Other marketplaces are not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteMarketplace} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.severity || 'success'} variant="filled" onClose={() => setNotice(null)} sx={{ color: '#fff' }}>
          {notice?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Sidebar;
